import type { SaveWorkflowInput, WorkflowDetailDto } from '../schemas/workflowSchemas';

// Serialise a fetched workflow detail back into the PUT /{workflowId} save body,
// preserving every step, binding, rule, condition and return field. Mirrors the
// mapping in SimpleWorkflowEditorScreen.handleSave so Rename / Duplicate round-trip
// a workflow without losing configuration. `overrides` lets callers change just the
// name (rename) or name + fresh ids (duplicate) without touching the rest.
export function detailToSaveInput(
  detail: WorkflowDetailDto,
  overrides?: { name?: string },
): SaveWorkflowInput {
  return {
    name: overrides?.name ?? detail.name,
    description: detail.description ?? null,
    language: detail.language ?? 'en',
    settingsJson: detail.settingsJson ?? null,
    steps: detail.steps.map((s, i) => ({
      id: s.id,
      stepType: s.stepType,
      displayName: s.displayName,
      position: i,
      isMandatory: s.isMandatory,
      isEnabled: s.isEnabled,
      settings: s.settings,
      apiBindings: s.apiBindings.map((b) => ({
        id: b.id,
        catalogueEntryId: b.catalogueEntryId,
        isEnabled: b.isEnabled,
        priority: b.priority,
        inputMappings: b.inputMappings.map((m) => ({
          id: m.id,
          apiParam: m.apiParam,
          sourceType: m.sourceType,
          sourceKey: m.sourceKey,
        })),
      })),
      riskRules: s.riskRules.map((r) => ({
        id: r.id,
        ruleKey: r.ruleKey,
        isEnabled: r.isEnabled,
        action: r.action,
        config: r.config,
      })),
      // Skip half-filled condition rows — the backend rejects empty conditions.
      conditions: s.conditions
        .filter((c) => c.ifField.trim() !== '' && c.ifValue.trim() !== '')
        .map((c, ci) => ({
          id: c.id,
          sortOrder: ci,
          ifField: c.ifField,
          operator: c.operator,
          ifValue: c.ifValue,
          thenAction: c.thenAction,
        })),
      returnFields: s.returnFields.map((f) => ({
        id: f.id,
        fieldKey: f.fieldKey,
        fieldGroup: f.fieldGroup,
        displayLabel: f.displayLabel,
        isIncluded: f.isIncluded,
        isDefaultIncluded: f.isDefaultIncluded,
      })),
    })),
  };
}
